import * as React from 'react';
import styled from 'styled-components';
import { Icon, Button } from "@mindlercare/ui-web";
import { defaultTheme } from '@mindlercare/ui-web/src/shared/constants';

export interface MessageInputProps {
  value: string;
  placeholder: string;
  disabled?: boolean;
  allowPasting?: boolean;
  onChange: (value: string) => void;
  onSend: () => void;
  sendAriaLabel?: string;
  informationMessage?: {
    text: string;
    severity: "info" | "warning";
  };
  onInformationMessageClose?: () => void;
}

type CreateTransient<Type> = {
  [Property in keyof Type as `$${string & Property}`]: Type[Property];
};

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

const BannerContainer = styled.div<CreateTransient<{ severity: "info" | "warning" }>>(({ $severity }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: defaultTheme.SPACING.SM,
  background: $severity === 'warning' ? defaultTheme.COLORS.TERTIARY_LIGHT_RED : defaultTheme.COLORS.TERTIARY_LIGHT,
  borderBottom: `1px solid ${defaultTheme.COLORS.GRAY_LIGHT}`,
  padding: `${defaultTheme.SPACING.SM} ${defaultTheme.SPACING.MD}`,
  fontSize: defaultTheme.FONT.SIZE.SMALL,
  color: defaultTheme.COLORS.GRAY_BLACK,
  fontFamily: defaultTheme.FONT.FAMILY.BODY,
}));

const BannerMessage = styled.span({
  flex: 1,
  lineHeight: 1.4,
  overflow: "hidden",
  color: defaultTheme.COLORS.GRAY,
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  fontFamily: defaultTheme.FONT.FAMILY.BODY,
});

const CloseButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: defaultTheme.SPACING.XS,
  color: defaultTheme.COLORS.GRAY,
  transition: 'color 0.2s ease',
  borderRadius: defaultTheme.BORDER_RADIUS.SM,

  '&:hover': {
    color: defaultTheme.COLORS.BLACK,
  },

  '& svg': {
    width: '16px',
    height: '16px',
  },
});

const ShellContainer = styled.div<CreateTransient<{ disabled?: boolean }>>(({ $disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: defaultTheme.SPACING.SM,
  background: defaultTheme.COLORS.WHITE,
  border: 'none',
  borderRadius: 0,
  padding: `${defaultTheme.SPACING.SM} ${defaultTheme.SPACING.MD}`,
  width: '100%',
  opacity: $disabled ? 0.9 : 1,
  boxSizing: 'border-box',
}));

const StyledTextarea = styled.textarea<CreateTransient<{ disabled?: boolean }>>(({ $disabled }) => ({
  flex: 1,
  minHeight: '38px',
  maxHeight: '160px',
  resize: 'none',
  background: 'transparent',
  color: $disabled ? defaultTheme.COLORS.GRAY : defaultTheme.COLORS.GRAY_BLACK,
  border: 'none',
  outline: 'none',
  padding: `${defaultTheme.SPACING.SM} ${defaultTheme.SPACING.SM}`,
  fontSize: '15px',
  lineHeight: 1.5,
  overflow: 'hidden',
  boxSizing: 'border-box',
  fontFamily: defaultTheme.FONT.FAMILY.BODY,
}));

export const MessageInput: React.FC<MessageInputProps> = ({
  value,
  placeholder,
  disabled,
  allowPasting = true,
  onChange,
  onSend,
  sendAriaLabel = 'Send message',
  informationMessage,
  onInformationMessageClose,
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  React.useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    // Reset height to allow shrinking
    el.style.height = 'auto';

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      const next = Math.min(180, el.scrollHeight);
      el.style.height = next + 'px';
    });
  }, [value]);

  const handleSend = React.useCallback(() => {
    if (disabled) return;
    if (!value.trim()) return;
    onSend();
  }, [disabled, value, onSend]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handlePaste = React.useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!allowPasting) {
      event.preventDefault();
    }
  }, [allowPasting]);

  const sendDisabled = disabled || !value.trim();

  return (
    <Container>
      {informationMessage && (
        <BannerContainer $severity={informationMessage.severity}>
          <BannerMessage>{informationMessage.text}</BannerMessage>
          {onInformationMessageClose && (
            <CloseButton
              onClick={onInformationMessageClose}
              aria-label="Close information message"
            >
              <Icon.Close />
            </CloseButton>
          )}
        </BannerContainer>
      )}
      <ShellContainer $disabled={disabled}>
        <StyledTextarea
          ref={textareaRef}
          rows={1}
          $disabled={disabled}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={!!disabled}
        />
        <Button onClick={handleSend} disabled={sendDisabled} aria-label={sendAriaLabel} variant="icon">
          <Icon.Send />
        </Button>
      </ShellContainer>
    </Container>
  );
};
